import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import HomePage from './pages/HomePage';
import SetupAgentPage from './pages/SetupAgentPage';
import SetupQueuePage from './pages/SetupQueuePage';
import BulkJobsPage from './pages/BulkJobsPage';
import BulkJobDetailPage from './pages/BulkJobDetailPage';
import SetupProgramDataPage from './pages/SetupProgramDataPage';
import SetupQuickConnectPage from './pages/SetupQuickConnectPage';
import SetupRoutingProfilePage from './pages/SetupRoutingProfilePage';
import SetupSecurityProfilePage from './pages/SetupSecurityProfilePage';
import SetupUserHierarchyGroupPage from './pages/SetupUserHierarchyGroupPage';
import SetupSendNotificationPage from './pages/SetupSendNotificationPage';
import AgentActivityReportPage from './pages/AgentActivityReportPage';
import QueuePerformanceReportPage from './pages/QueuePerformanceReportPage';
import RoutingAdminPage from './pages/RoutingAdminPage';
import BusinessAdminPage from './pages/BusinessAdminPage';
import ArchiveManagementPage from './pages/ArchiveManagementPage';
import CreateArchivePage from './pages/CreateArchivePage';
import CompareArchivesPage from './pages/CompareArchivesPage';
import RestoreArchivePage from './pages/RestoreArchivePage';
import AttributeRegistryPage from './pages/AttributeRegistryPage';
import RoutingPoliciesPage from './pages/RoutingPoliciesPage';
import RoutingRestrictionsPage from './pages/RoutingRestrictionsPage';
import RoutingGroupsPage from './pages/RoutingGroupsPage';
import RoutingSimulationPage from './pages/RoutingSimulationPage';
import RoutingDashboardPage from './pages/RoutingDashboardPage';
import PrototypeAgentReRoutePage from './pages/PrototypeAgentReRoutePage';
//import HoldTimeAnalysisReportPage from './pages/HoldTimeAnalysisReportPage';
//import ContactCustomerJourneyReportPage from './pages/ContactCustomerJourneyReportPage';
//import ConfigurationGapReportPage from './pages/ConfigurationGapReportPage';
import ReportsPage from './pages/ReportsPage';
import ViewAgentPage from './pages/ViewAgentPage';
import LoginPage from './pages/LoginPage';
import LogoffPage from './pages/LogoffPage';
import './App.css';

/**
 * Wrapper for /home route: if Okta callback params (code + state) are present,
 * render LoginPage to handle the token exchange; otherwise render the protected HomePage.
 */
function HomeOrCallback() {
  const [searchParams] = useSearchParams();
  const hasCallback = searchParams.has('code') && searchParams.has('state');
  const hasError = searchParams.has('error') && searchParams.has('state');

  if (hasCallback || hasError) {
    return <LoginPage />;
  }

  return (
    <PrivateRoute>
      <Layout><HomePage /></Layout>
    </PrivateRoute>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Public routes without Layout */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/logoff" element={<LogoffPage />} />
        
        {/* Protected routes with Layout */}
        <Route 
          path="/" 
          element={
            <PrivateRoute>
              <Layout><HomePage /></Layout>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/home" 
          element={<HomeOrCallback />} 
        />
        <Route 
          path="/setup-agent" 
          element={
            <PrivateRoute>
              <Layout><SetupAgentPage /></Layout>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/setup-queue" 
          element={
            <PrivateRoute>
              <Layout><SetupQueuePage /></Layout>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/business-admin" 
          element={
            <PrivateRoute>
              <Layout><BusinessAdminPage /></Layout>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/archive-management" 
          element={
            <PrivateRoute>
              <Layout><ArchiveManagementPage /></Layout>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/compare-archives" 
          element={
            <PrivateRoute>
              <Layout><CompareArchivesPage /></Layout>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/create-archive" 
          element={
            <PrivateRoute>
              <Layout><CreateArchivePage /></Layout>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/restore-archive" 
          element={
            <PrivateRoute>
              <Layout><RestoreArchivePage /></Layout>
            </PrivateRoute>
          } 
        />
         <Route 
          path="/prototype-agent-re-route" 
          element={
            <PrivateRoute>
              <Layout><PrototypeAgentReRoutePage /></Layout>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/bulk-jobs" 
          element={
            <PrivateRoute>
              <Layout><BulkJobsPage /></Layout>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/bulk-jobs/:jobId" 
          element={
            <PrivateRoute>
              <Layout><BulkJobDetailPage /></Layout>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/setup-program-data" 
          element={
            <PrivateRoute>
              <Layout><SetupProgramDataPage /></Layout>
            </PrivateRoute>
          } 
        />
        <Route
          path="/setup-quick-connect"
          element={
            <PrivateRoute>
              <Layout><SetupQuickConnectPage /></Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/setup-routing-profile"
          element={
            <PrivateRoute>
              <Layout><SetupRoutingProfilePage /></Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/setup-security-profile"
          element={
            <PrivateRoute>
              <Layout><SetupSecurityProfilePage /></Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/setup-user-hierarchy-group"
          element={
            <PrivateRoute>
              <Layout><SetupUserHierarchyGroupPage /></Layout>
            </PrivateRoute>
          }
        />
         <Route
          path="/view-agents"
          element={
            <PrivateRoute>
              <Layout><ViewAgentPage /></Layout>
            </PrivateRoute>
          }
        />  
         <Route
          path="/reports"
          element={
            <PrivateRoute>
              <Layout><ReportsPage /></Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/routing-admin"
          element={
            <PrivateRoute>
              <Layout><RoutingAdminPage /></Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/setup-send-notification"
          element={
            <PrivateRoute>
              <Layout><SetupSendNotificationPage /></Layout>
            </PrivateRoute>
          }
        />
         <Route
          path="/agent-activity-report"
          element={
            <PrivateRoute>
              <Layout><AgentActivityReportPage /></Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/queue-performance-report"
          element={
            <PrivateRoute>
              <Layout><QueuePerformanceReportPage /></Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/routing-dashboard"
          element={
            <PrivateRoute>
              <Layout><RoutingDashboardPage /></Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/attribute-registry"
          element={
            <PrivateRoute>
              <Layout><AttributeRegistryPage /></Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/routing-policies"
          element={
            <PrivateRoute>
              <Layout><RoutingPoliciesPage /></Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/routing-restrictions"
          element={
            <PrivateRoute>
              <Layout><RoutingRestrictionsPage /></Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/routing-groups"
          element={
            <PrivateRoute>
              <Layout><RoutingGroupsPage /></Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/routing-simulation"
          element={
            <PrivateRoute>
              <Layout><RoutingSimulationPage /></Layout>
            </PrivateRoute>
          }
        />
        {/* <Route
          path="/holdtime-analysis-report"
          element={
            <PrivateRoute>
              <Layout><HoldTimeAnalysisReportPage /></Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/contact-customer-journey-report"
          element={
            <PrivateRoute>
              <Layout><ContactCustomerJourneyReportPage /></Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/configuration-gap-report"
          element={
            <PrivateRoute>
              <Layout><ConfigurationGapReportPage /></Layout>
            </PrivateRoute>
          } */}
        {/* Redirect any unknown routes to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
